import { useEffect, useRef } from 'react';
import { useSiteBanner } from './useSiteBanner';
import { CHIP_TYPE } from '@types';

const POLL_INTERVAL_MS = 60_000;
const VERSION_URL = '/version.json';

export const useVersionCheck = () => {
	const { showBanner } = useSiteBanner();
	const buildSha = import.meta.env.VITE_BUILD_SHA;
	const dismissedShaRef = useRef<string | null>(null);
	const shownShaRef = useRef<string | null>(null);
	const stoppedRef = useRef(false);

	useEffect(() => {
		if (!buildSha) return;

		const check = async () => {
			if (stoppedRef.current) return;
			let res: Response;
			try {
				res = await fetch(VERSION_URL, { cache: 'no-store' });
			} catch {
				return;
			}
			if (!res.ok) {
				stoppedRef.current = true;
				return;
			}
			const data = await res.json().catch(() => null);
			const remoteSha: string | undefined = data?.sha;
			if (!remoteSha || remoteSha === buildSha) return;
			if (dismissedShaRef.current === remoteSha) return;
			if (shownShaRef.current === remoteSha) return;
			shownShaRef.current = remoteSha;
			showBanner({
				message: 'A new version of Ofload is available.',
				tone: CHIP_TYPE.INFO,
				actions: [
					{
						label: 'Refresh',
						onClick: () => window.location.reload()
					}
				],
				onDismiss: () => {
					dismissedShaRef.current = remoteSha;
				}
			});
		};

		check();
		const id = setInterval(check, POLL_INTERVAL_MS);
		return () => clearInterval(id);
	}, [buildSha, showBanner]);
};
